import { useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  makeStyles,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../../api';
import type {
  Repository,
  RepositoryBranch,
  RepositoryOwner,
  RepositoryProject,
  ScmProviderDescriptor,
} from '../../api/types';

const useStyles = makeStyles(theme => ({
  formControl: {
    marginBottom: theme.spacing(2),
  },
  spinner: {
    marginLeft: theme.spacing(1),
  },
}));

export interface RepoHierarchyValue {
  owner: RepositoryOwner | null;
  project: RepositoryProject | null;
  repository: Repository | null;
  branch: RepositoryBranch | null;
}

export interface RepoHierarchyPickerProps {
  provider: ScmProviderDescriptor;
  value: RepoHierarchyValue;
  onChange: (value: RepoHierarchyValue) => void;
  disabled?: boolean;
  /**
   * Restringe os owners exibidos (ex.: só aqueles onde o usuário tem
   * `activity.create`). Ausente = todos os owners do provider.
   */
  filterOwners?: (owner: RepositoryOwner) => boolean;
  /**
   * Restringe os repositórios exibidos (ex.: membership limitada a repos
   * específicos). Ausente = todos os repositórios do owner/projeto.
   */
  filterRepositories?: (repository: Repository) => boolean;
}

/**
 * Adaptive picker that expresses a repository selection through whatever
 * hierarchy the provider declares.
 *
 * Steps:
 *   1. Owner — uses `provider.hierarchy.ownerLabel`. For Azure DevOps this is
 *      effectively a single value (the configured organization), so the field
 *      is rendered but pre-filled.
 *   2. Project — only shown when `provider.hierarchy.hasProject === true`.
 *   3. Repository
 *   4. Branch
 *
 * Every dropdown loads via the agnostic `/scm/*` endpoints — no Azure-specific
 * call paths here. Other providers plug in by registering a different
 * `RepositoryProvider` on the backend.
 */
export function RepoHierarchyPicker({
  provider,
  value,
  onChange,
  disabled,
  filterOwners,
  filterRepositories,
}: RepoHierarchyPickerProps): JSX.Element {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);

  const [owners, setOwners] = useState<RepositoryOwner[]>([]);
  const [projects, setProjects] = useState<RepositoryProject[]>([]);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [branches, setBranches] = useState<RepositoryBranch[]>([]);
  const [loading, setLoading] = useState({
    owners: false,
    projects: false,
    repositories: false,
    branches: false,
  });

  // Load owners on mount / provider change.
  useEffect(() => {
    let cancelled = false;
    setLoading(s => ({ ...s, owners: true }));
    api
      .listScmOwners(provider.id)
      .then(items => {
        if (!cancelled) setOwners(items);
      })
      .finally(() => {
        if (!cancelled) setLoading(s => ({ ...s, owners: false }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.id]);

  // Load projects when owner changes (only for providers with project layer).
  useEffect(() => {
    if (!provider.hierarchy.hasProject || !value.owner) {
      setProjects([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(s => ({ ...s, projects: true }));
    api
      .listScmProjects(provider.id, value.owner.id)
      .then(items => {
        if (!cancelled) setProjects(items);
      })
      .finally(() => {
        if (!cancelled) setLoading(s => ({ ...s, projects: false }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.id, value.owner?.id]);

  // Load repositories when owner (or project, when applicable) changes.
  useEffect(() => {
    if (!value.owner) {
      setRepositories([]);
      return undefined;
    }
    if (provider.hierarchy.hasProject && !value.project) {
      setRepositories([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(s => ({ ...s, repositories: true }));
    api
      .listScmRepositories(provider.id, value.owner.id, value.project?.name)
      .then(items => {
        if (!cancelled) setRepositories(items);
      })
      .finally(() => {
        if (!cancelled) setLoading(s => ({ ...s, repositories: false }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.id, value.owner?.id, value.project?.id]);

  // Load branches when repository changes.
  useEffect(() => {
    if (!value.repository) {
      setBranches([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(s => ({ ...s, branches: true }));
    api
      .listScmBranches(provider.id, {
        externalId: value.repository.externalId,
        owner: value.repository.owner,
        project: value.repository.project,
        name: value.repository.name,
      })
      .then(items => {
        if (cancelled) return;
        setBranches(items);
        if (!value.branch) {
          const def = items.find(b => b.isDefault) ?? items[0];
          if (def) onChange({ ...value, branch: def });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(s => ({ ...s, branches: false }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.id, value.repository?.externalId]);

  const ownerLabel = provider.hierarchy.ownerLabel;
  const projectLabel = provider.hierarchy.projectLabel ?? 'Project';

  // Filtros aplicados no render, não no carregamento: os predicados podem
  // mudar depois que a lista chegou (ex.: roles do RBAC resolvendo após o
  // picker abrir) e a lista precisa refletir a restrição na hora.
  const visibleOwners = useMemo(
    () => (filterOwners ? owners.filter(filterOwners) : owners),
    [owners, filterOwners],
  );
  const visibleRepositories = useMemo(
    () =>
      filterRepositories
        ? repositories.filter(filterRepositories)
        : repositories,
    [repositories, filterRepositories],
  );

  // Auto-select when only one owner — typical for Azure DevOps where the
  // configured organization is the only entry, ou quando a restrição RBAC
  // deixa um único owner visível.
  useEffect(() => {
    if (visibleOwners.length === 1 && !value.owner) {
      onChange({
        ...value,
        owner: visibleOwners[0],
        project: null,
        repository: null,
        branch: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleOwners, value.owner]);

  const ownerSelected = visibleOwners.length === 1;

  return (
    <>
      {!ownerSelected && (
        <FormControl
          variant="outlined"
          size="small"
          fullWidth
          className={classes.formControl}
          disabled={disabled}
        >
          <InputLabel id="repo-owner-label">{ownerLabel}</InputLabel>
          <Select
            labelId="repo-owner-label"
            label={ownerLabel}
            data-testid="repo-owner-select"
            value={value.owner?.id ?? ''}
            onChange={ev => {
              const next =
                visibleOwners.find(o => o.id === ev.target.value) ?? null;
              onChange({
                owner: next,
                project: null,
                repository: null,
                branch: null,
              });
            }}
          >
            {visibleOwners.map(o => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
          {loading.owners && (
            <CircularProgress size={16} className={classes.spinner} />
          )}
        </FormControl>
      )}

      {provider.hierarchy.hasProject && (
        <FormControl
          variant="outlined"
          size="small"
          fullWidth
          className={classes.formControl}
          disabled={disabled || !value.owner}
        >
          <InputLabel id="repo-project-label">{projectLabel}</InputLabel>
          <Select
            labelId="repo-project-label"
            label={projectLabel}
            data-testid="repo-project-select"
            value={value.project?.id ?? ''}
            onChange={ev => {
              const next = projects.find(p => p.id === ev.target.value) ?? null;
              onChange({
                ...value,
                project: next,
                repository: null,
                branch: null,
              });
            }}
          >
            {projects.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
          {loading.projects && (
            <CircularProgress size={16} className={classes.spinner} />
          )}
        </FormControl>
      )}

      <FormControl
        variant="outlined"
        size="small"
        fullWidth
        className={classes.formControl}
        disabled={
          disabled ||
          !value.owner ||
          (provider.hierarchy.hasProject && !value.project)
        }
      >
        <InputLabel id="repo-repository-label">Repositório</InputLabel>
        <Select
          labelId="repo-repository-label"
          label="Repositório"
          data-testid="repo-repository-select"
          value={value.repository?.externalId ?? ''}
          onChange={ev => {
            const next =
              visibleRepositories.find(r => r.externalId === ev.target.value) ??
              null;
            onChange({ ...value, repository: next, branch: null });
          }}
        >
          {visibleRepositories.map(r => (
            <MenuItem key={r.externalId} value={r.externalId}>
              {r.name}
            </MenuItem>
          ))}
        </Select>
        {loading.repositories && (
          <CircularProgress size={16} className={classes.spinner} />
        )}
      </FormControl>

      <FormControl
        variant="outlined"
        size="small"
        fullWidth
        className={classes.formControl}
        disabled={disabled || !value.repository}
      >
        <InputLabel id="repo-branch-label">Branch</InputLabel>
        <Select
          labelId="repo-branch-label"
          label="Branch"
          data-testid="repo-branch-select"
          value={value.branch?.name ?? ''}
          onChange={ev => {
            const next = branches.find(b => b.name === ev.target.value) ?? null;
            onChange({ ...value, branch: next });
          }}
        >
          {branches.map(b => (
            <MenuItem key={b.name} value={b.name}>
              {b.name}
              {b.isDefault ? ' (default)' : ''}
            </MenuItem>
          ))}
        </Select>
        {loading.branches && (
          <CircularProgress size={16} className={classes.spinner} />
        )}
      </FormControl>
    </>
  );
}
